import React, { useState } from 'react';
import { toast } from 'react-toastify';
import {
  FaSpinner, FaCheck, FaShieldAlt, FaExclamationTriangle, FaUniversity
} from 'react-icons/fa';
import { API_URL } from '../config';

// ============================================
// BANK DETAILS CONFIRMATION MODAL (subbie side)
// Steps:
//   1. Save / update bank details (encrypted server-side)
//   2. Confirm the exact account number + liability checkbox
//   3. Status: PENDING → CONFIRMED → VERIFIED_FOR_QBO (NEEDS_REVIEW on change)
// ============================================

const STATUS_META = {
  PENDING: { label: 'Not yet confirmed', color: '#92400E', bg: '#FEF3C7' },
  CONFIRMED: { label: 'Confirmed by you — awaiting admin verify', color: '#1E40AF', bg: '#DBEAFE' },
  VERIFIED_FOR_QBO: { label: 'Verified for QuickBooks', color: '#065F46', bg: '#D1FAE5' },
  NEEDS_REVIEW: { label: 'Change under review by One Purpose', color: '#991B1B', bg: '#FEE2E2' }
};

const BankDetailsConfirmModal = ({ open, onClose, onDone }) => {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState('form'); // form | confirm
  const [form, setForm] = useState({ account_holder: '', account_number: '', bank_name: '', branch_code: '', account_type: 'Savings' });
  const [typedAccount, setTypedAccount] = useState('');
  const [liabilityAgreed, setLiabilityAgreed] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [confirming, setConfirming] = useState(false);

  const fetchStatus = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/bank-details/status`);
      const data = await res.json();
      if (data.success && data.bank_details) {
        setStatus(data.bank_details);
        if (data.bank_details.has_bank_details) setStep('confirm');
      }
    } catch (e) {
      console.error('bank status fetch failed', e);
    } finally {
      setLoading(false);
    }
  };

  // Load status when opened
  React.useEffect(() => {
    if (open) {
      setStep('form');
      setTypedAccount('');
      setLiabilityAgreed(false);
      fetchStatus();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);


  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const res = await fetch(`${API_URL}/bank-details/submit`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form)
      });
      const data = await res.json();
      if (data.success) {
        toast.success(data.message);
        setStep('confirm');
        await fetchStatus();
      } else {
        toast.error(data.error || 'Failed to save bank details');
      }
    } catch (err) {
      console.error(err);
      toast.error('Failed to save bank details');
    } finally {
      setSubmitting(false);
    }
  };

  const handleConfirm = async () => {
    setConfirming(true);
    try {
      const res = await fetch(`${API_URL}/bank-details/confirm`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ confirm_account_number: typedAccount, liability_agreed: liabilityAgreed })
      });
      const data = await res.json();
      if (data.success) {
        toast.success(data.message);
        await fetchStatus();
        if (onDone) onDone();
      } else {
        toast.error(data.error || 'Confirmation failed');
      }
    } catch (err) {
      console.error(err);
      toast.error('Confirmation failed');
    } finally {
      setConfirming(false);
    }
  };

  if (!open) return null;

  const meta = STATUS_META[status?.status] || STATUS_META.PENDING;


  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="fixed inset-0 bg-black/50" onClick={onClose}></div>
      <div className="relative bg-white rounded-2xl shadow-2xl max-w-lg w-full mx-4 p-6 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-bold text-navy flex items-center gap-2">
            <FaUniversity className="text-green-600" /> Bank Details
          </h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-xl">✕</button>
        </div>

        {loading ? (
          <div className="py-10 text-center text-gray-400"><FaSpinner className="animate-spin inline" size={22} /></div>
        ) : (
          <>
            <div className="mb-4 p-3 rounded-xl flex items-center gap-2 text-sm" style={{ backgroundColor: meta.bg, color: meta.color }}>
              <FaShieldAlt size={16} /> {meta.label}
            </div>

            {status?.status === 'NEEDS_REVIEW' && (
              <div className="mb-4 p-3 rounded-xl bg-amber-50 text-amber-800 text-sm flex items-start gap-2">
                <FaExclamationTriangle className="mt-0.5" size={16} />
                <span>{status.review_reason || 'Your changed bank details are being reviewed by a One Purpose admin before they can be used for payments.'}</span>
              </div>
            )}

            {status?.status === 'VERIFIED_FOR_QBO' && (
              <div className="mb-4 p-3 rounded-xl bg-green-50 text-green-800 text-sm">
                ✅ {status.bank_name ? `Bank: ${status.bank_name} · Account: ${status.masked_account}` : 'Your bank details are verified and will be used for payments.'}
              </div>
            )}


            {step === 'form' && (
              <form onSubmit={handleSubmit}>
                <div className="grid grid-cols-1 gap-3 mb-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Account Holder Name *</label>
                    <input
                      type="text"
                      value={form.account_holder}
                      onChange={(e) => setForm({ ...form, account_holder: e.target.value })}
                      required
                      className="w-full px-3 py-2 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-green-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Account Number *</label>
                    <input
                      type="text"
                      inputMode="numeric"
                      value={form.account_number}
                      onChange={(e) => setForm({ ...form, account_number: e.target.value })}
                      required
                      placeholder="7–12 digits"
                      className="w-full px-3 py-2 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-green-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Bank Name *</label>
                    <input
                      type="text"
                      value={form.bank_name}
                      onChange={(e) => setForm({ ...form, bank_name: e.target.value })}
                      required
                      placeholder="e.g. FNB, Standard Bank, ABSA"
                      className="w-full px-3 py-2 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-green-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Branch Code *</label>
                    <input
                      type="text"
                      inputMode="numeric"
                      value={form.branch_code}
                      onChange={(e) => setForm({ ...form, branch_code: e.target.value })}
                      required
                      placeholder="6 digits"
                      className="w-full px-3 py-2 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-green-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Account Type</label>
                    <select
                      value={form.account_type}
                      onChange={(e) => setForm({ ...form, account_type: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-200 rounded-xl text-sm"
                    >
                      <option>Savings</option>
                      <option>Cheque</option>
                      <option>Current</option>
                      <option>Transmission</option>
                    </select>
                  </div>
                </div>
                <p className="text-xs text-gray-400 mb-4">
                  🔒 Your details are encrypted and only used to pay your invoices. They are never shown in full to other users.
                </p>
                <div className="flex justify-end gap-3">
                  <button type="button" onClick={onClose} className="px-4 py-2 text-sm text-gray-500 hover:bg-gray-100 rounded-lg">Cancel</button>
                  <button type="submit" disabled={submitting} className="px-4 py-2 bg-green text-white rounded-lg text-sm font-medium hover:bg-green disabled:opacity-50 flex items-center gap-2">
                    {submitting ? <FaSpinner className="animate-spin" size={14} /> : <FaCheck size={14} />} Save & Continue
                  </button>
                </div>
              </form>
            )}


            {step === 'confirm' && (
              <div>
                <div className="mb-4 p-3 rounded-xl bg-gray-50 text-sm text-gray-700">
                  Saved: <strong>{status?.bank_name || form.bank_name}</strong> · Account <strong>{status?.masked_account || '***'}</strong>
                </div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Confirm by typing the <strong>full account number</strong> exactly as saved:
                </label>
                <input
                  type="text"
                  inputMode="numeric"
                  value={typedAccount}
                  onChange={(e) => setTypedAccount(e.target.value)}
                  placeholder="Re-type full account number"
                  className="w-full px-3 py-2 border border-gray-200 rounded-xl text-sm mb-4 focus:outline-none focus:ring-2 focus:ring-green-500"
                />
                <label className="flex items-start gap-2 text-sm text-gray-600 mb-4 p-3 bg-amber-50 rounded-xl">
                  <input
                    type="checkbox"
                    checked={liabilityAgreed}
                    onChange={(e) => setLiabilityAgreed(e.target.checked)}
                    className="mt-1"
                  />
                  <span>
                    I confirm these bank details belong to my company and I accept that payment will be made to this account. I understand that changing them later requires One Purpose admin approval.
                  </span>
                </label>
                <div className="flex justify-end gap-3">
                  <button onClick={() => setStep('form')} className="px-4 py-2 text-sm text-gray-500 hover:bg-gray-100 rounded-lg">Edit details</button>
                  <button onClick={handleConfirm} disabled={confirming || !typedAccount || !liabilityAgreed} className="px-4 py-2 bg-green text-white rounded-lg text-sm font-medium hover:bg-green disabled:opacity-50 flex items-center gap-2">
                    {confirming ? <FaSpinner className="animate-spin" size={14} /> : <FaCheck size={14} />} Confirm Account
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default BankDetailsConfirmModal;
